import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'constants/QueryKeys';
import { client } from 'api/baseAxios';
import { Auth } from 'api/Auth';

export const useAuthContext = () => {
  const queryClient = useQueryClient();
  const [localToken, setLocalToken] = useState<string | null>(localStorage.getItem('token'));

  const setToken = useCallback((newToken: string) => {
    setLocalToken(newToken);
    localStorage.setItem('token', newToken);
  }, [setLocalToken]);

  const clearToken = useCallback(() => {
    setLocalToken(null);
    localStorage.removeItem('token');
  }, [setLocalToken]);

  const {
    data: user, isLoading, isFetched,
  } = useQuery({
    queryKey: [queryKeys.VERIFY_TOKEN],
    queryFn: async () => client(Auth).getAuth(),
    retry: false,
    staleTime: Infinity,
    enabled: !!localToken,
  });

  const {
    data: privileges, isLoading: isLoadingPrivileges,
  } = useQuery({
    queryKey: [queryKeys.PRIVILEGES_LIST],

    queryFn: async () => {
      const list = await client(Auth).privilegesList();
      return list?.privileges ?? [];
    },

    retry: false,
    staleTime: Infinity,
    enabled: !!localToken,
  });

  const logOut = useCallback(() => {
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(queryKeys.ENV_CONFIG);
    clearToken();
    queryClient.clear();
    queryClient.removeQueries({ queryKey: [queryKeys.VERIFY_TOKEN] });
  }, [clearToken, queryClient]);

  useEffect(() => {
    if (!localToken || (isFetched && !user)) {
      logOut();
    }
  }, [isFetched, user, localToken, logOut]);

  return {
    user,
    loadingUser: isLoading,
    isLoggedIn: !!user,
    privileges,
    loadingPrivileges: isLoadingPrivileges,
    hasToken: () => !!localToken,
    setToken,
    clearToken,
    logOut,
  };
};
